// ContextPanel.jsx — unified right panel for the map editor.
// Content is determined solely by selection state and active placement mode.
//
// Priority order (first match wins):
//   1. tactical marking selected        → TacticalMarkingEditor
//   2. selected marking has sidc        → SymbolEditor
//   3. selected marking (no sidc)       → plain shape form (color / label)
//   4. default (nothing selected)       → ElementPalette (five-tab icon palette)
//   Bottom always                       → OptionsPanel collapsible accordion

import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {Box, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, TextField, Select, MenuItem, Divider, Chip, Tooltip} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import SymbolEditor from '../../mapEditor/SymbolEditor';
import TacticalMarkingEditor from '../../mapEditor/TacticalMarkingEditor';
import OptionsPanel from '../../mapEditor/OptionsPanel';
import ElementPalette from '../../mapEditor/ElementPalette';
import {getMobilityForSidc} from './renderer/SymbolRenderer';
import {STALE_BADGE_TEXT} from './renderer/staleRendering';
import {formatTimestamp} from '../../../utils/formatTimestamp';
import UserAvatar from '../../UserAvatar';
import {getUsersByIds} from '../../../firebase/firestore/users';

/**
 * Resolves a list of user IDs to their profile objects ({id, displayName, hasAvatar, ...}).
 * Results are cached in a ref to avoid redundant API calls across re-renders.
 * Returns a Map<userId, profile>.
 */
function useUserProfiles(ids) {
    const [profiles, setProfiles] = useState({});
    const cacheRef = useRef({});

    useEffect(() => {
        const unique = (ids || []).filter(Boolean);
        const missing = unique.filter((id) => !cacheRef.current[id]);
        if (!missing.length) return;

        let cancelled = false;
        getUsersByIds(missing)
            .then((users) => {
                if (cancelled) return;
                const next = {...cacheRef.current};
                users.forEach((u) => {
                    if (u?.id) next[u.id] = u;
                });
                missing.forEach((id) => {
                    if (!next[id]) next[id] = null;
                });
                cacheRef.current = next;
                setProfiles({...next});
            })
            .catch(() => {});
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids?.join(',')]);

    return profiles;
}

// -- Plain shape form (no SIDC) ------------------------------------------------
// rerender-memo: memoized so it skips re-render when marking/onUpdateProperties are unchanged.
const ShapeForm = React.memo(function ShapeForm({marking, onUpdateProperties}) {
    const color  = marking?.properties?.color  || '#3b82f6';
    const label  = marking?.properties?.label  || '';

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            <Stack direction="row" alignItems="center" sx={(theme) => ({
                px: 1.5, py: 0.75,
                borderBottom: `1px solid ${theme.palette.divider}`,
                flexShrink: 0, bgcolor: 'background.default',
            })}>
                <Typography variant="caption" sx={{fontWeight: 700, flex: 1, fontSize: 11, letterSpacing: 0.3, textTransform: 'uppercase', color: 'text.disabled'}}>
                    Shape
                </Typography>
            </Stack>
            <Box sx={{flex: 1, overflowY: 'auto', px: 1.5, py: 1}}>
                <Stack gap={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{width: 80, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>Color</Typography>
                        <input
                            type="color"
                            value={color}
                            onChange={(e) => onUpdateProperties?.({color: e.target.value})}
                            style={{width: 32, height: 24, border: 'none', background: 'none', cursor: 'pointer', padding: 0}}
                            aria-label="Shape color"
                        />
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{width: 80, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>Label</Typography>
                        <TextField
                            size="small" fullWidth
                            value={label}
                            onChange={(e) => onUpdateProperties?.({label: e.target.value})}
                            inputProps={{style: {fontSize: 12, padding: '3px 8px'}}}
                        />
                    </Stack>
                </Stack>
            </Box>
        </Stack>
    );
});

// -- Marking identity/mobility metadata (ticket 03) ---------------------------
// Shared across all three marking-editor variants (SymbolEditor / TacticalMarkingEditor /
// ShapeForm) so createdBy/createdAt/updatedBy/updatedAt display and the mobility override
// live in exactly one place instead of being duplicated per-editor. Rendered by ContextPanel
// directly below whichever editor is active, whenever a marking is selected.

const MOBILITY_OPTIONS = [
    {value: 'auto', label: 'Auto'},
    {value: 'stationary', label: 'Stationary'},
    {value: 'moving', label: 'Moving'},
];

/** Inline user display: avatar + resolved name, with UUID-prefix fallback while loading. */
function UserDisplay({userId, profiles}) {
    const profile = userId ? profiles[userId] : null;
    const displayName = profile?.displayName
        || (profile?.firstName || profile?.lastName
            ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
            : null)
        || profile?.email
        || null;

    const label = displayName || (userId ? userId.slice(0, 8) + '…' : '—');
    const loading = userId && profiles[userId] === undefined;

    return (
        <Tooltip title={userId || ''} disableInteractive>
            <Stack component="span" direction="row" alignItems="center" gap={0.5} sx={{display: 'inline-flex', verticalAlign: 'middle'}}>
                {userId && (
                    <UserAvatar
                        userId={userId}
                        user={profile || undefined}
                        sx={{width: 16, height: 16, fontSize: 9}}
                    />
                )}
                <Box component="span" sx={{color: 'text.primary', fontStyle: loading ? 'italic' : 'normal'}}>
                    {label}
                </Box>
            </Stack>
        </Tooltip>
    );
}

const MarkingMetaPanel = React.memo(function MarkingMetaPanel({marking, onUpdateProperties}) {
    const userIds = useMemo(
        () => marking ? [marking.author, marking.updatedBy].filter(Boolean) : [],
        [marking?.author, marking?.updatedBy],
    );
    const profiles = useUserProfiles(userIds);

    if (!marking) return null;

    // ticket 03: mobility is only meaningful for SIDC-backed symbols (installations vs.
    // units/equipment) — hide the override control for plain shapes/free-draw markings that
    // have no `sidc`, since getMobilityForSidc(null) is always null for them anyway.
    const showMobilityControl = Boolean(marking.sidc) || marking.mobility != null;
    const mobilityValue = marking.mobility === 'stationary' || marking.mobility === 'moving' ? marking.mobility : 'auto';

    const handleMobilityChange = (next) => {
        // "Auto" re-derives from the current SIDC right now rather than just storing null,
        // so switching back to Auto immediately reflects what auto-derivation would produce.
        const mobility = next === 'auto' ? getMobilityForSidc(marking.sidc) : next;
        onUpdateProperties?.({mobility});
    };

    return (
        <Box sx={{px: 1.5, py: 1}}>
            <Divider sx={{mb: 1}}/>
            <Typography variant="caption" sx={{
                display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                textTransform: 'uppercase', color: 'text.disabled', mb: 0.5,
            }}>
                Tracking
            </Typography>

            {showMobilityControl && (
                <Stack direction="row" alignItems="center" gap={1} sx={{minHeight: 34}}>
                    <Typography variant="caption" sx={{width: 80, flexShrink: 0, color: 'text.secondary', fontSize: 11}}>
                        Mobility
                    </Typography>
                    <Select
                        size="small" fullWidth value={mobilityValue}
                        onChange={(e) => handleMobilityChange(e.target.value)}
                        sx={{fontSize: 12, '& .MuiSelect-select': {py: '4px', fontSize: 12}}}
                    >
                        {MOBILITY_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value} sx={{fontSize: 12}}>{o.label}</MenuItem>
                        ))}
                    </Select>
                </Stack>
            )}

            <Stack gap={0.25} sx={{mt: showMobilityControl ? 1 : 0}}>
                <Typography variant="caption" sx={{fontSize: 11, color: 'text.secondary'}}>
                    Created by{' '}<UserDisplay userId={marking.author} profiles={profiles}/>
                    {' · '}{formatTimestamp(marking.createdAt, '—')}
                </Typography>
                {(marking.updatedBy || marking.updatedAt) && (
                    <Typography variant="caption" sx={{fontSize: 11, color: 'text.secondary'}}>
                        Updated by{' '}<UserDisplay userId={marking.updatedBy} profiles={profiles}/>
                        {' · '}{formatTimestamp(marking.updatedAt, '—')}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
});

// -- Options accordion wrapper ------------------------------------------------
// rerender-memo: has no props, so once mounted it never needs to re-render
// due to parent updates (e.g. the 60fps placement-progress RAF poll in MapEditorModal).
const OptionsAccordion = React.memo(function OptionsAccordion() {
    const [open, setOpen] = useState(false);
    return (
        <Accordion
            disableGutters elevation={0} square expanded={open} onChange={(_, v) => setOpen(v)}
            TransitionProps={{timeout: 0}}
            sx={(theme) => ({
                flexShrink: 0,
                borderTop: `1px solid ${theme.palette.divider}`,
                '&:before': {display: 'none'},
                bgcolor: 'background.paper',
            })}
        >
            <AccordionSummary expandIcon={<ExpandMoreRounded sx={{fontSize: 14}}/>}
                sx={{px: 1.5, minHeight: 32, '& .MuiAccordionSummary-content': {my: 0}}}>
                <Typography variant="caption" sx={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                    textTransform: 'uppercase', color: 'text.disabled',
                }}>
                    Symbol Options
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{p: 0, maxHeight: 260, overflowY: 'auto'}}>
                {/* ponytail: OptionsPanel has its own scroll container; cap height to avoid layout overflow */}
                <Box sx={{height: 260}}>
                    <OptionsPanel/>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
});


// -- ContextPanel -------------------------------------------------------------
// rerender-memo: parent (MapEditorModal) re-renders on every vertex placed while a tactical
// placement is in progress (event-driven placedCount updates); memoizing avoids re-running
// this component's branch logic and re-rendering the whole right sidebar on every placement.
const ContextPanel = React.memo(function ContextPanel({
    pendingSidc,
    onSelectSidc,
    pendingTacticalSidc,
    onSelectTacticalSidc,
    selectedMarkings,
    onUpdateMarkingProps,
    onDeselectAll,
    onFlyToCoordinate,
}) {
    const [paletteTabIndex, setPaletteTabIndex] = useState(0);
    const [paletteSearch, setPaletteSearch]     = useState('');

    const primaryMarking = selectedMarkings?.[0] ?? null;
    // ticket 03: `flattenMarkingForPanel` (useMapEditor.js) resolves the selected marking's data
    // against the live DTG cursor and stamps `isCurrent` from `resolveTrackAtDtg` onto it. When
    // the cursor doesn't land on an exact snapshot, show the same "LAST KNOWN" badge treatment
    // used for stale map/tactical-graphic rendering (renderer/staleRendering.js) here near the
    // panel header, rather than inventing a new indicator.
    // ticket 04 (merge-last-known-into-trail): `primaryMarking.visible` (from
    // flattenMarkingForPanel) is `false` when the resolved snapshot is stale beyond the
    // trail-aware suppression threshold (trail off, or trail on but past trailWindowDays) — mirror
    // that suppression here so the panel never shows a "LAST KNOWN" badge for a marking that's
    // fully hidden on the map itself.
    const showStaleBadge = Boolean(primaryMarking) && primaryMarking.isCurrent === false && primaryMarking.visible !== false;

    // rerender-memo: stable object identity for feature editors below (memo-friendly).
    const primaryAsFeature = useMemo(() => (
        primaryMarking
            ? {id: primaryMarking.markingId, properties: primaryMarking, geometry: primaryMarking.geometry}
            : null
    ), [primaryMarking]);

    // rerender-functional-setstate / advanced-event-handler-refs: one stable callback shared
    // by all three editors instead of a fresh arrow function per render per editor.
    const handleUpdateProperties = useCallback((props) => {
        if (primaryMarking) onUpdateMarkingProps?.(primaryMarking.markingId, props);
    }, [primaryMarking, onUpdateMarkingProps]);

    let mainContent;

    if (primaryAsFeature?.properties?.elementType?.startsWith('tactical')) {
        // Tactical marking selected — show dimension modifier editor
        mainContent = (
            <TacticalMarkingEditor
                feature={primaryAsFeature}
                onUpdateProperties={handleUpdateProperties}
                onClose={onDeselectAll}
            />
        );
    } else if (primaryAsFeature?.properties?.sidc) {
        mainContent = (
            <SymbolEditor
                feature={primaryAsFeature}
                onUpdateProperties={handleUpdateProperties}
                onClose={onDeselectAll}
                onFlyToCoordinate={onFlyToCoordinate}
            />
        );
    } else if (primaryAsFeature) {
        mainContent = (
            <ShapeForm
                marking={primaryAsFeature}
                onUpdateProperties={handleUpdateProperties}
            />
        );
    } else {
        mainContent = (
            <ElementPalette
                activeTabIndex={paletteTabIndex}
                onTabChange={setPaletteTabIndex}
                search={paletteSearch}
                onSearchChange={setPaletteSearch}
                pendingSidc={pendingSidc}
                onSelectSidc={onSelectSidc}
                pendingTacticalSidc={pendingTacticalSidc}
                onSelectTacticalSidc={onSelectTacticalSidc}
            />
        );
    }

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            {showStaleBadge && (
                <Stack direction="row" alignItems="center" gap={0.75} sx={(theme) => ({
                    px: 1.5, py: 0.375,
                    bgcolor: 'warning.50',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                })}>
                    <Chip
                        label={STALE_BADGE_TEXT}
                        size="small"
                        sx={{height: 14, fontSize: 9, fontWeight: 700, letterSpacing: 0.3, px: 0.25, bgcolor: 'warning.100', color: 'warning.dark'}}
                    />
                    <Typography variant="caption" sx={{fontSize: 10, color: 'warning.dark'}}>
                        Showing the nearest earlier snapshot — not the exact DTG
                    </Typography>
                </Stack>
            )}
            <Box sx={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0}}>
                {mainContent}
            </Box>
            {primaryAsFeature && (
                <MarkingMetaPanel marking={primaryAsFeature.properties} onUpdateProperties={handleUpdateProperties}/>
            )}
            <OptionsAccordion/>
        </Stack>
    );
});

export default ContextPanel;
